// ---------------------------------------------------------------------------
// SERVICE LAYER — single point of contact between UI and data.
// Today it resolves against the local mock store. To move to the NestJS API,
// flip USE_MOCK to false and each method already maps to its REST route.
// ---------------------------------------------------------------------------
import * as mock from '../mock/data';

const USE_MOCK = false;
export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolves a media path returned by the API (e.g. "/uploads/cells/xyz.jpg")
// into an absolute URL. Passes external URLs (mock/legacy data) through untouched.
export function mediaUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//.test(path)) return path;
  return `${process.env.REACT_APP_BACKEND_URL}${path}`;
}

// Future REST routes (documented for the NestJS handoff):
//   GET  /networks
//   GET  /cells            ?q&neighborhood&city&networkId
//   GET  /events
//   POST /events/:id/register   { name, phone, email, answers }
//   GET  /events/:id/export?format=csv|xlsx
//   GET  /gallery          ?category
//   POST /auth/login       { email, password } -> { token, user }

const delay = (ms = 320) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------------
// Sessão — o AuthContext persiste a resposta do login real do backend
// ({ accessToken, refreshToken, user }) em localStorage['av_auth']. Esses
// helpers centralizam a leitura do token (fonte única) e a limpeza da sessão.
// A chave legada 'authToken' continua servindo como fallback para sessões
// antigas criadas antes da integração com o backend.
// ---------------------------------------------------------------------------
const AUTH_STORAGE_KEY = 'av_auth';

function getStoredToken() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.accessToken) return parsed.accessToken; // login real (backend)
      if (parsed?.token) return parsed.token; // sessões antigas do mock
    }
  } catch (e) {
    /* storage corrompido: cai para a chave legada abaixo */
  }
  return localStorage.getItem('authToken');
}

function clearSession() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
}

function getAuthHeaders() {
  const token = getStoredToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function fetchWithAuth(url, options = {}) {
  const headers = { ...getAuthHeaders(), ...options.headers };
  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    clearSession();
    window.location.href = '/admin/login';
    throw new Error('Sessão expirada. Faça login novamente.');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Erro na requisição' }));
    throw new Error(err.message || `Erro ${res.status}`);
  }

  return res.json();
}

// In-memory mutable copies so the admin panel can create records in the demo.
let _cells = [...mock.cells];
let _events = mock.events.map((e) => ({ ...e }));
let _gallery = [...mock.gallery];
let _networks = mock.networks.map((n) => ({ ...n }));
let _homeSections = mock.homeSections.map((s) => ({ ...s, content: { ...s.content } }));
let _preachingChannels = mock.preachingChannels.map((c) => ({ ...c }));
let _preachings = mock.preachings.map((p) => ({ ...p }));

// eventId -> [{name, phone, email, answers, registeredAt}] — seeded so the report has data.
const SEED_NAMES = [
  ['Ana Beatriz Souza', '33988801122', 'ana.souza@email.com'],
  ['Carlos Eduardo Lima', '33988802233', 'cadu.lima@email.com'],
  ['Juliana Ferreira', '33988803344', 'ju.ferreira@email.com'],
  ['Marcos Vinícius', '33988804455', ''],
  ['Patrícia Gomes', '33988805566', 'paty.gomes@email.com'],
  ['Rodrigo Alves', '33988806677', ''],
  ['Beatriz Martins', '33988807788', 'bia.martins@email.com'],
];
const _registrations = {};
mock.events.forEach((ev) => {
  const n = Math.min(ev.filledSlots, SEED_NAMES.length);
  _registrations[ev.id] = SEED_NAMES.slice(0, n).map(([name, phone, email], i) => ({
    name, phone, email,
    answers: {},
    registeredAt: new Date(Date.now() - (i + 1) * 86400000).toISOString(),
    checkedIn: i % 3 === 0,
  }));
});

function replaceIn(list, id, patch) {
  return list.map((x) => (x.id === id ? { ...x, ...patch } : x));
}

function downloadBlob(content, filename, mime) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  async getChurch() {
    if (USE_MOCK) { await delay(80); return mock.church; }
  },

  async getNetworks() {
    if (USE_MOCK) { await delay(120); return _networks; }
    return fetchWithAuth(`${API_BASE}/networks`);
  },

  async getCells() {
    if (USE_MOCK) { await delay(280); return _cells; }
    return fetchWithAuth(`${API_BASE}/cells`);
  },

  async getCell(id) {
    if (USE_MOCK) { await delay(160); return _cells.find((c) => c.id === id) || null; }
    return fetchWithAuth(`${API_BASE}/cells/${id}`);
  },

  async getEvents() {
    if (USE_MOCK) { await delay(260); return _events; }
    return fetchWithAuth(`${API_BASE}/events`);
  },

  async registerForEvent(eventId, payload) {
    if (USE_MOCK) {
      await delay(500);
      const ev = _events.find((e) => e.id === eventId);
      if (!ev) throw new Error('Evento não encontrado');
      if (ev.filledSlots >= ev.totalSlots) throw new Error('Evento esgotado');
      // Valida campos dinâmicos obrigatórios
      const fields = Array.isArray(ev.formFields) ? ev.formFields : [];
      const answers = payload.answers || {};
      for (const field of fields) {
        if (field.required) {
          const v = answers[field.key];
          const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
          if (empty) throw new Error(`O campo "${field.label}" é obrigatório`);
        }
      }
      ev.filledSlots += 1;
      (_registrations[eventId] = _registrations[eventId] || []).push({
        name: payload.name,
        phone: payload.phone,
        email: payload.email,
        answers,
        registeredAt: new Date().toISOString(),
      });
      return { ok: true, remaining: ev.totalSlots - ev.filledSlots };
    }
    return fetchWithAuth(`${API_BASE}/events/${eventId}/register`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async exportRegistrations(eventId, format) {
    if (USE_MOCK) {
      await delay(200);
      const ev = _events.find((e) => e.id === eventId);
      const rows = _registrations[eventId] || [];
      const fields = Array.isArray(ev?.formFields) ? ev.formFields : [];
      const header = ['Nome', 'Telefone', 'Email', 'Data de inscrição', 'Presença', ...fields.map((f) => f.label)];
      const data = rows.map((r) => {
        const a = r.answers || {};
        return [
          r.name, r.phone || '', r.email || '',
          r.registeredAt ? new Date(r.registeredAt).toLocaleString('pt-BR') : '',
          r.checkedIn ? 'Presente' : 'Ausente',
          ...fields.map((f) => a[f.key] ?? ''),
        ];
      });
      const slug = (ev?.title || 'evento').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = '\uFEFF' + [header, ...data].map((row) => row.map(esc).join(',')).join('\n');
      // No mock, gera CSV (lista). O XLSX real é gerado pelo backend.
      downloadBlob(csv, `inscritos-${slug}.csv`, 'text/csv;charset=utf-8;');
      return { ok: true };
    }
    const token = getStoredToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/events/${eventId}/export?format=${format}`, { headers });
    if (res.status === 401) {
      clearSession();
      window.location.href = '/admin/login';
      throw new Error('Sessão expirada');
    }
    if (!res.ok) throw new Error('Falha ao exportar');
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : `inscritos.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
    downloadBlob(blob, filename, res.headers.get('Content-Type') || 'application/octet-stream');
    return { ok: true };
  },

  async getRegistrations(eventId) {
    if (USE_MOCK) {
      await delay(220);
      return _registrations[eventId] || [];
    }
    return fetchWithAuth(`${API_BASE}/events/${eventId}/registrations`);
  },

  async setCheckIn(eventId, index, value) {
    if (USE_MOCK) {
      await delay(100);
      const list = _registrations[eventId];
      if (list && list[index]) list[index].checkedIn = value;
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/events/${eventId}/registrations/${index}/checkin`, {
      method: 'PATCH',
      body: JSON.stringify({ checkedIn: value }),
    });
  },

  async getGallery() {
    if (USE_MOCK) { await delay(240); return _gallery; }
    return fetchWithAuth(`${API_BASE}/gallery`);
  },

  async getMissions() {
    if (USE_MOCK) { await delay(200); return mock.missions; }
    return fetchWithAuth(`${API_BASE}/missions`);
  },

  async getPreachingChannels() {
    if (USE_MOCK) { await delay(120); return _preachingChannels; }
    return fetchWithAuth(`${API_BASE}/preaching-channels`);
  },

  async getPreachings() {
    if (USE_MOCK) { await delay(260); return _preachings; }
    return fetchWithAuth(`${API_BASE}/preachings`);
  },

  // ---- Home Sections (configuração dinâmica da Home) ----
  async getHomeSections() {
    if (USE_MOCK) {
      await delay(200);
      return [..._homeSections]
        .sort((a, b) => a.order - b.order)
        .filter((s) => s.visible);
    }
    return fetchWithAuth(`${API_BASE}/home-sections`);
  },

  async getAllHomeSections() {
    if (USE_MOCK) {
      await delay(180);
      return [..._homeSections].sort((a, b) => a.order - b.order);
    }
    return fetchWithAuth(`${API_BASE}/home-sections/all`);
  },

  async createHomeSection(section) {
    if (USE_MOCK) {
      await delay(280);
      const maxOrder = _homeSections.reduce((m, s) => Math.max(m, s.order), 0);
      const record = { ...section, id: `hs-${Date.now()}`, order: section.order ?? maxOrder + 1 };
      _homeSections = [..._homeSections, record];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/home-sections`, {
      method: 'POST',
      body: JSON.stringify(section),
    });
  },
  async updateHomeSection(id, patch) {
    if (USE_MOCK) {
      await delay(260);
      _homeSections = replaceIn(_homeSections, id, patch);
      return _homeSections.find((s) => s.id === id);
    }
    return fetchWithAuth(`${API_BASE}/home-sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deleteHomeSection(id) {
    if (USE_MOCK) {
      await delay(220);
      _homeSections = _homeSections.filter((s) => s.id !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/home-sections/${id}`, { method: 'DELETE' });
  },
  async reorderHomeSection(id, direction) {
    if (USE_MOCK) {
      await delay(200);
      const sorted = [..._homeSections].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((s) => s.id === id);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return { ok: false };
      const a = sorted[idx];
      const b = sorted[swapIdx];
      const aOrder = a.order;
      a.order = b.order;
      b.order = aOrder;
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/home-sections/${id}/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ direction }),
    });
  },

  async login(email, password) {
    if (USE_MOCK) {
      await delay(420);
      const u = mock.adminUser;
      if (email.trim().toLowerCase() === u.email && password === u.password) {
        return {
          token: `mock.jwt.${btoa(u.email)}.${Date.now()}`,
          user: { id: u.id, name: u.name, email: u.email },
        };
      }
      throw new Error('E-mail ou senha inválidos');
    }
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Erro no login' }));
      throw new Error(err.message || `Erro ${res.status}`);
    }
    return res.json();
  },

  // ---- Admin mutations (demo, in-memory). Map 1:1 to future NestJS routes. ----
  // Redes
  async createNetwork(net) {
    if (USE_MOCK) {
      await delay(280);
      const record = { ...net, id: `net-${Date.now()}` };
      _networks = [..._networks, record];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/networks`, {
      method: 'POST',
      body: JSON.stringify(net),
    });
  },
  async updateNetwork(id, patch) {
    if (USE_MOCK) {
      await delay(260);
      _networks = replaceIn(_networks, id, patch);
      return _networks.find((n) => n.id === id);
    }
    return fetchWithAuth(`${API_BASE}/networks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deleteNetwork(id) {
    if (USE_MOCK) {
      await delay(220);
      _networks = _networks.filter((n) => n.id !== id);
      _cells = _cells.filter((c) => c.networkId !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/networks/${id}`, { method: 'DELETE' });
  },

  // Células
  async createCell(cell) {
    if (USE_MOCK) {
      await delay(300);
      const record = { ...cell, id: `cell-${Date.now()}` };
      _cells = [record, ..._cells];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/cells`, {
      method: 'POST',
      body: JSON.stringify(cell),
    });
  },
  async updateCell(id, patch) {
    if (USE_MOCK) {
      await delay(280);
      _cells = replaceIn(_cells, id, patch);
      return _cells.find((c) => c.id === id);
    }
    return fetchWithAuth(`${API_BASE}/cells/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deleteCell(id) {
    if (USE_MOCK) {
      await delay(220);
      _cells = _cells.filter((c) => c.id !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/cells/${id}`, { method: 'DELETE' });
  },

  // Mídia da célula (líder, anfitrião, reuniões) — limites (2/2/5) validados no backend.
  async uploadCellMedia(id, category, files) {
    const form = new FormData();
    form.append('category', category);
    Array.from(files).forEach((file) => form.append('files', file));
    const token = getStoredToken();
    const res = await fetch(`${API_BASE}/cells/${id}/media`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      if (res.status === 401) {
        clearSession();
        window.location.href = '/admin/login';
        throw new Error('Sessão expirada. Faça login novamente.');
      }
      const err = await res.json().catch(() => ({ message: 'Falha ao enviar fotos' }));
      throw new Error(err.message || `Erro ${res.status}`);
    }
    return res.json();
  },
  async deleteCellMedia(id, category, url) {
    return fetchWithAuth(
      `${API_BASE}/cells/${id}/media?category=${encodeURIComponent(category)}&url=${encodeURIComponent(url)}`,
      { method: 'DELETE' },
    );
  },

  // Eventos
  async createEvent(ev) {
    if (USE_MOCK) {
      await delay(300);
      const record = { ...ev, id: `ev-${Date.now()}`, filledSlots: ev.filledSlots || 0, formFields: ev.formFields || [] };
      _events = [record, ..._events];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/events`, {
      method: 'POST',
      body: JSON.stringify(ev),
    });
  },
  async updateEvent(id, patch) {
    if (USE_MOCK) {
      await delay(280);
      _events = replaceIn(_events, id, patch);
      return _events.find((e) => e.id === id);
    }
    return fetchWithAuth(`${API_BASE}/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deleteEvent(id) {
    if (USE_MOCK) {
      await delay(220);
      _events = _events.filter((e) => e.id !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/events/${id}`, { method: 'DELETE' });
  },

  // Galeria / Fotos
  async createGalleryItem(item) {
    if (USE_MOCK) {
      await delay(280);
      const record = { ...item, id: `g-${Date.now()}` };
      _gallery = [record, ..._gallery];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/gallery`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  },
  async updateGalleryItem(id, patch) {
    if (USE_MOCK) {
      await delay(260);
      _gallery = replaceIn(_gallery, id, patch);
      return _gallery.find((g) => g.id === id);
    }
    return fetchWithAuth(`${API_BASE}/gallery/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deleteGalleryItem(id) {
    if (USE_MOCK) {
      await delay(220);
      _gallery = _gallery.filter((g) => g.id !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/gallery/${id}`, { method: 'DELETE' });
  },


  // Pregações
  async createPreaching(p) {
    if (USE_MOCK) {
      await delay(280);
      const record = { ...p, id: `pr-${Date.now()}` };
      _preachings = [record, ..._preachings];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/preachings`, {
      method: 'POST',
      body: JSON.stringify(p),
    });
  },
  async updatePreaching(id, patch) {
    if (USE_MOCK) {
      await delay(260);
      _preachings = replaceIn(_preachings, id, patch);
      return _preachings.find((x) => x.id === id);
    }
    return fetchWithAuth(`${API_BASE}/preachings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deletePreaching(id) {
    if (USE_MOCK) {
      await delay(220);
      _preachings = _preachings.filter((x) => x.id !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/preachings/${id}`, { method: 'DELETE' });
  },

  // Canais de pregação
  async createPreachingChannel(ch) {
    if (USE_MOCK) {
      await delay(280);
      const record = { ...ch, id: `ch-${Date.now()}` };
      _preachingChannels = [..._preachingChannels, record];
      return record;
    }
    return fetchWithAuth(`${API_BASE}/preaching-channels`, {
      method: 'POST',
      body: JSON.stringify(ch),
    });
  },
  async updatePreachingChannel(id, patch) {
    if (USE_MOCK) {
      await delay(260);
      _preachingChannels = replaceIn(_preachingChannels, id, patch);
      return _preachingChannels.find((x) => x.id === id);
    }
    return fetchWithAuth(`${API_BASE}/preaching-channels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deletePreachingChannel(id) {
    if (USE_MOCK) {
      await delay(220);
      _preachingChannels = _preachingChannels.filter((x) => x.id !== id);
      return { ok: true };
    }
    return fetchWithAuth(`${API_BASE}/preaching-channels/${id}`, { method: 'DELETE' });
  },
};
